import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { convertCurrency, formatCurrency } from '../../utils/currencyUtils';

const TableContainer = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px;
  padding: 12px 8px;
  margin-top: 16px;
  margin-left: auto;     // Центрирование контейнера
  margin-right: auto;    // Центрирование контейнера
  max-width: 95%;        // Ограничение ширины
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.05),
              -4px -4px 8px rgba(255, 255, 255, 0.8);
  overflow-x: auto;
  
  @media (max-width: 576px) {
    padding: 8px 4px;
    max-width: 98%;     // На мобильных устройствах используем всю доступную ширину
  }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  
  th, td {
    padding: 10px 4px; // Увеличиваем вертикальный отступ
    text-align: left;
    font-size: 15px; // Сохраняем базовый размер шрифта
    font-weight: 400; // Нормальная толщина для лучшей читаемости
    letter-spacing: 0.01em; // Небольшой трекинг для лучшей читаемости
  }
  
  th {
    font-weight: 600;
    color: #4a6fa1;
    border-bottom: 1px solid #e6ebf5;
  }
  
  tr:hover {
    background-color: #f5f6fa;
  }
  
  td {
    border-bottom: 1px solid #f0f3f9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .transaction-number {
    width: 30px;
  }
  .transaction-date {
    width: 75px;
    padding-right: 8px; // Добавляем отступ справа от колонки с датой
  }
  .transaction-category {
    min-width: 110px;
    width: auto;
    max-width: 45%;
    padding-left: 8px; // Добавляем отступ слева от колонки с категорией
  }
  .transaction-amount {
    width: 90px;      // Увеличиваем ширину для суммы
    text-align: right;
    padding-right: 20px; // Добавляем правый отступ между суммой и кнопкой
  }
  
  .transaction-actions {
    width: 35px;
    text-align: center;
    padding-left: 4px; // Добавляем левый отступ для колонки с кнопкой
  }
  
  @media (max-width: 576px) {
    th, td {
      padding: 8px 3px;
      font-size: 13px;
    }
    
    .transaction-number {
      width: 25px;
    }
    
    .transaction-date {
      width: 70px; // Увеличиваем для года
    }
    
    .transaction-category {
      min-width: 85px;
      max-width: 42%;
    }
    
    .transaction-amount {
      width: 85px; // Увеличиваем для отображения оригинальной суммы
    }
    
    .transaction-actions {
      width: 30px;
    }
  }
  
  @media (max-width: 420px) {
    .transaction-number {
      display: none;
    }
    
    .transaction-date {
      width: 65px;
    }
    
    .transaction-amount {
      width: 80px;
    }
    
    .transaction-actions {
      width: 25px;
    }
  }
`;

// Улучшаем шрифт в контейнере даты
const DateContainer = styled.div`
  .date {
    font-weight: 500;
    color: #333;
    font-size: 12px; // Увеличиваем размер шрифта
    white-space: nowrap;
    letter-spacing: 0.01em; // Улучшаем трекинг
  }
  
  .time {
    font-size: 11px; // Увеличиваем размер шрифта
    color: #4a6fa1; // Улучшаем контрастность
    display: block;
    white-space: nowrap;
    margin-top: 2px;
  }
  
  @media (max-width: 576px) {
    .date {
      font-size: 11px;
    }
    
    .time {
      font-size: 10px;
    }
  }
`;

// Улучшаем шрифт категории
const CategoryContainer = styled.div`
  .category-name {
    font-weight: 500;
    color: #333;
    font-size: 15px; // Увеличиваем размер шрифта
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    letter-spacing: 0.01em; // Улучшаем трекинг
  }
  
  .category-comment {
    font-size: 12px; // Увеличиваем размер шрифта
    color: #4a6fa1; // Улучшаем контрастность
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  @media (max-width: 576px) {
    .category-name {
      font-size: 13px;
    }
    
    .category-comment {
      font-size: 10px;
      margin-top: 2px;
    }
  }
`;

// Улучшаем шрифт суммы
const AmountContainer = styled.div`
  text-align: right;
  
  .converted-amount {
    font-weight: 500;
    color: #333;
    white-space: nowrap;
    font-size: 14px; // Увеличиваем размер шрифта
    letter-spacing: 0.01em; // Улучшаем трекинг
  }
  
  .original-amount {
    font-size: 11px; // Увеличиваем размер шрифта
    color: #4a6fa1; // Улучшаем контрастность
    white-space: nowrap;
    margin-top: 2px;
  }
  
  @media (max-width: 576px) {
    .converted-amount {
      font-size: 12px;
    }
    
    .original-amount {
      font-size: 10px;
    }
  }
`;

// 3. Уменьшаем размер кнопки удаления
const DeleteButton = styled.button`
  background: #ff7e7e;
  color: white;
  border: none;
  padding: 4px 6px; // Уменьшаем отступы с 6px 8px
  border-radius: 4px; // Уменьшаем радиус с 5px
  cursor: pointer;
  font-size: 11px; // Уменьшаем размер шрифта с 12px
  font-weight: 500; 
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 16px; // Уменьшаем с 20px
  min-height: 24px; // Уменьшаем с 26px
  
  &:hover {
    background: #ff5c5c;
    transform: scale(1.03); // Уменьшаем эффект с 1.05
    box-shadow: 0 1px 3px rgba(255, 94, 94, 0.3); // Меньшая тень
  }
  
  &:active {
    transform: scale(0.98);
    background: #ff4a4a;
  }
  
  .icon {
    width: 12px; // Уменьшаем с 13px
    height: 12px; // Уменьшаем с 13px
    display: inline-block;
  }
  
  .text {
    display: inline-block;
    font-size: 10px; // Уменьшаем с 11px
    margin-left: 3px; // Уменьшаем с 4px
    letter-spacing: 0.01em;
  }
  
  @media (max-width: 576px) {
    padding: 4px; // Уменьшаем с 6px
    min-height: 18px; // Уменьшаем с 20px
    
    .icon {
      width: 12px; // Уменьшаем с 13px
      height: 12px; // Уменьшаем с 13px
    }
    
    .text {
      display: none;
    }
  }
  
  @media (max-width: 420px) {
    min-height: 24px; // Уменьшаем с 30px
    min-width: 24px; // Уменьшаем с 30px
    border-radius: 4px; // Уменьшаем с 6px
    
    .icon {
      width: 14px; // Уменьшаем с 16px
      height: 14px; // Уменьшаем с 16px
    }
  }
`;



const LoadingOverlay = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #8c96a5;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #8c96a5;
`;

// Иконка удаления
const TrashIcon = () => (
  <svg className="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
    {/* Добавляем дополнительную линию для лучшей различимости */}
    <path d="M10 10h1v6h-1zm3 0h1v6h-1z"/>
  </svg>
);

const TransactionsTable = ({ 
  transactions = [], 
  exchangeRates = {}, 
  preferredCurrency = 'UAH', 
  onDelete,
  isLoading 
}) => {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  
  // Отслеживаем изменение размера окна для реактивного поведения
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Определяем размеры экрана для адаптивной вёрстки
  const isSmallMobile = windowWidth <= 420;
  const isMobile = windowWidth <= 576;
  
  // Функция форматирования даты и времени
  const formatDateTime = (dateString) => {
  if (!dateString) return { date: '-', time: '' };
  
  try {
    // Разбиваем строку на дату и время
    const parts = dateString.split(' ');
    if (parts.length !== 2) {
      return { date: dateString, time: '' };
    }
    
    const datePart = parts[0];
    const timePart = parts[1];
    
    let formattedDate = datePart;
    let formattedTime = '';
    
    // Преобразуем формат даты с добавлением года в виде двух цифр
    if (datePart.includes('-')) {
      const dateParts = datePart.split('-');
      if (dateParts.length === 3) {
        const [year, month, day] = dateParts;
        // Отображаем день, месяц и последние две цифры года
        const shortYear = year.slice(-2); // Получаем последние 2 цифры года
        formattedDate = `${day}.${month}.${shortYear}`;
      }
    } else if (datePart.includes('.')) {
      const dateParts = datePart.split('.');
      if (dateParts.length === 3) {
        const [day, month, year] = dateParts;
        // Отображаем день, месяц и последние две цифры года
        const shortYear = year.slice(-2); // Получаем последние 2 цифры года
        formattedDate = `${day}.${month}.${shortYear}`;
      }
    }
    
    // Ограничиваем время часами и минутами
    if (timePart) {
      const timeLimit = 5; // "HH:MM" имеет 5 символов
      formattedTime = timePart.substring(0, timeLimit);
    }
    
    return {
      date: formattedDate,
      time: formattedTime
    };
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return { date: dateString || '-', time: '' };
  }
};
  
  // Сортируем транзакции по дате в обратном порядке
  const sortedTransactions = [...(transactions || [])].sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return new Date(b.date) - new Date(a.date);
  });
  

  
  if (isLoading) {
    return (
    <TableContainer>
      <LoadingOverlay>{t('loading_table')}</LoadingOverlay>
    </TableContainer>
    );
  }
  
  if (!transactions || transactions.length === 0) {
    return (
    <TableContainer>
      <EmptyState>{t('no_transactions')}</EmptyState>
    </TableContainer>
    );
  }
  
  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            {!isSmallMobile && <th className="transaction-number">#</th>}
            <th className="transaction-date">{t('tableHeaders.date')}</th>
            <th className="transaction-category">{t('tableHeaders.category')}</th>
            <th className="transaction-amount">{t('tableHeaders.amount')}</th>
            <th className="transaction-actions"></th>
          </tr>
        </thead>
        <tbody>
          {sortedTransactions.map((transaction, index) => {
            // Форматируем дату и время
            const formattedDateTime = formatDateTime(transaction.date);
            
            // Конвертируем сумму, если валюта отличается от предпочитаемой
            const amount = parseFloat(transaction.amount) || 0;
            const originalCurrency = transaction.currency || 'UAH';
            
            // Безопасная конвертация валюты
            let convertedAmount = amount;
            if (originalCurrency !== preferredCurrency) {
              try {
                convertedAmount = convertCurrency(
                  amount,
                  originalCurrency,
                  preferredCurrency,
                  exchangeRates
                );
              } catch (error) {
                console.error('Conversion error:', error);
              }
            }
            
            // Форматируем суммы для отображения
            const formattedOriginalAmount = formatCurrency(amount, originalCurrency);
            const formattedConvertedAmount = formatCurrency(convertedAmount, preferredCurrency);
            
            return (
  <tr key={transaction.id || index}>
    {!isSmallMobile && <td className="transaction-number">{sortedTransactions.length - index}</td>}
    
    <td className="transaction-date">
      <DateContainer>
        <div className="date">{formattedDateTime.date}</div>
        <div className="time">{formattedDateTime.time}</div>
      </DateContainer>
    </td>
    
    <td className="transaction-category">
      <CategoryContainer>
        <div className="category-name">{transaction.category}</div>
        {/* Отображаем комментарий всегда, если он есть */}
        {transaction.comment && (
          <div className="category-comment">{transaction.comment}</div>
        )}
      </CategoryContainer>
    </td>
    
    <td className="transaction-amount">
      <AmountContainer>
        <div className="converted-amount">{formattedConvertedAmount}</div>
        {/* Отображаем исходную сумму всегда, если валюта отличается */}
        {originalCurrency !== preferredCurrency && (
          <div className="original-amount">{formattedOriginalAmount}</div>
        )}
      </AmountContainer>
    </td>
    
    <td className="transaction-actions">
      <DeleteButton onClick={() => onDelete(transaction.id)}>
        <TrashIcon />
        <span className="text">{t('tableHeaders.delete')}</span>
      </DeleteButton>
    </td>
  </tr>
);
          })}
        </tbody>
      </Table>
    </TableContainer>
  );
};

export default TransactionsTable;